import React from "react";
import { FormHelperText, Grid, Link } from "@material-ui/core";
import styled from "styled-components";
import { withRouter, RouteComponentProps } from "react-router-dom";
import WithLoadingSpinner from "../hoc/WithLoadingSpinner";
import { connect } from "react-redux";
import { IApplicationState } from "../../store";
import { selectAuth } from "../../store/auth/selectors";
import Services from "../../services/Services";
import ISupportCall from "../../models/ISupportCall";
import { IMenuItem } from "../../models/IMenuItem";
import Theme from "../../Theme";
import { getErrorsGrid, processErrors } from "../../functions/errorHandling";
import SandboxRequestText, { SandboxTypes } from "./sandbox/SandboxRequestText";
import IntegrationSelect from "../integrations/IntegrationSelect";
import SupportTicketCategorySelect from "./SupportTicketCategorySelect";
import { SupportTicketCategoryType } from "../../services/ISupportTicketCategoryRepository";
import ISupportTicketCategory from "../../models/ISupportTicketCategory";
import SubmitSupportTicketForm from "./SubmitSupportTicketForm";
import OneRosterSandboxRequestForm from "./sandbox/OneRosterSandboxRequestForm";
import FullSandboxRequestForm from "./sandbox/FullSandboxRequestForm";
import ServiceCallAPIFields from "./api/ServiceCallAPIFields";
import { GetAPIMarkdown, GetMarkdown } from "../../functions/formFunctions";
import getTicketTitle from "../forms/GetTicketTitle";
import { getSMSAPIHelperText } from "./api/APIHelperText";

const FormGrid = styled(Grid)`
  &&& .MuiFormLabel-asterisk {
    color: ${Theme.colors.error};
  }
`;

const CategorySelect = styled(SupportTicketCategorySelect)`
  min-width: 475px;
  max-width: 600px;
`;

const StyledIntegrationSelect = styled(IntegrationSelect)`
  min-width: 475px;
  max-width: 600px;
`;

export interface IServiceCallForm extends ISupportCall {
  ServiceRequestTitle: string;
}

interface ISubmitServiceCallFormProps extends RouteComponentProps {
  token: string;
  username: string;
  isServiceRequest: boolean;
}

const dateFormat = { year: "numeric", month: "numeric", day: "numeric" };

const SubmitServiceCallForm = (props: ISubmitServiceCallFormProps) => {
  const { isServiceRequest, username, token, history } = props;

  const [integration, setIntegration] = React.useState<IMenuItem | null>(null);
  const [category, setCategory] = React.useState<ISupportTicketCategory | undefined>();
  const [errors, setErrors] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [url, setUrl] = React.useState<string>("");
  const [apiErrorMessage, setApiErrorMessage] = React.useState<string>("");
  const [customers, setCustomers] = React.useState<string>("");

  const handleSubmit = async (summary: string, description: string): Promise<boolean> => {
    try {
      if (!category || !category.MenuPath || category.Name === "-") {
        throw Error("Please select a Service Request");
      }
      var call: ISupportCall = {
        id: "0",
        status: "New",
        summary: summary,
        description: GetAPIMarkdown(getTicketTitle(category.MenuPath, summary), description, url, apiErrorMessage, customers),
        dateSubmitted: new Date().toUTCString(),
        submittedBy: username || "partner portal unknown user",
        type: "External",
        menuPath: category.MenuPath,
        product: "",
        productType: "",
        labelPath: "",
        module: "",
        object: "",
        screen: "",
        integrationID: integration ? String(integration.ID) : "",
      };

      setIsLoading(true);
      const scRepo = await new Services().ServiceCallRepository();
      let returnedServiceCall = await scRepo.SubmitServiceCall(token, call);

      setIsLoading(false);
      history.push("/submit-sc-confirmation", returnedServiceCall);
      return true;
    } catch (error) {
      setIsLoading(false);
      setErrors(processErrors(error));
      return false;
    }
  };

  const getFormContents = (): React.ReactNode => {
    if (category && category.MenuPath.startsWith("IH\\PR\\PS\\PS\\SR")) {
      switch (category.MenuPath) {
        case "IH\\PR\\PS\\PS\\SR\\SR":
          return <OneRosterSandboxRequestForm category={category} onFormSubmit={handleSubmit} />;
        case "IH\\PR\\PS\\PS\\SR\\FS":
          return <FullSandboxRequestForm category={category} onFormSubmit={handleSubmit} />;
        default:
          return (
            <SubmitSupportTicketForm
              onFormSubmit={handleSubmit}
              submitButtonText="Submit Request"
              prefilledDescription={props.location.state && props.location.state.prefilledDescription}
            />
          );
      }
    }
    return (
      <SubmitSupportTicketForm
        onFormSubmit={handleSubmit}
        submitButtonText="Ask for Help"
        prefilledDescription={props.location.state && props.location.state.prefilledDescription}
      />
    );
  };

  const getHelperText = () => {
    if (category && category.MenuPath) {
      switch (category.MenuPath) {
        case "IH\\PR\\PS\\PS\\SR\\SR":
          return SandboxRequestText(SandboxTypes.OneRoster);
        case "IH\\PR\\PS\\PS\\SR\\FS":
          return SandboxRequestText(SandboxTypes.Full);
        default:
          return null;
      }
    }
  };

  const getAPIFields = () => {
    if (category && category.MenuPath) {
      switch (category.MenuPath) {
        case "IH\\PR\\PS\\PS\\ST\\QG":
        case "IH\\PR\\PS\\PS\\ST\\QR":
        case "IH\\PR\\PS\\PS\\ST\\QO":
        case "IH\\PR\\PS\\PS\\ST\\SL":
        case "IH\\PR\\PS\\PS\\ST\\SR":
          return <ServiceCallAPIFields onCustomersFieldChange={setCustomers} onErrorMessageFieldChange={setApiErrorMessage} onURLFieldChange={setUrl} />;
        default:
          return null;
      }
    }
  };

  const isSMSAPI = (): boolean =>
    category && (category.MenuPath === "IH\\PR\\PS\\PS\\ST\\SL" || category.MenuPath === "IH\\PR\\PS\\PS\\ST\\SR") ? true : false;

  return (
    <WithLoadingSpinner message="Submitting ticket" isLoading={isLoading}>
      <Grid container direction="row-reverse" justify="flex-end" alignItems="flex-start" spacing={3}>
        <Grid item md={5}>
          {getHelperText()}
        </Grid>
        <Grid item md={7}>
          <FormGrid container direction="column" alignItems="flex-start" spacing={3}>
            <Grid item>{getErrorsGrid(errors)}</Grid>
            <Grid item>
              <StyledIntegrationSelect onValueChange={setIntegration} />
            </Grid>
            <Grid item>
              <CategorySelect
                type={isServiceRequest ? SupportTicketCategoryType.ServiceRequest : SupportTicketCategoryType.SupportTicket}
                category={category}
                onMenuPathChange={setCategory}
              />
            </Grid>
            {isSMSAPI() && getSMSAPIHelperText()}
            {getAPIFields()}
            {getFormContents()}
          </FormGrid>
        </Grid>
      </Grid>
    </WithLoadingSpinner>
  );
};

export default connect((state: IApplicationState) => {
  return selectAuth(state);
})(withRouter(SubmitServiceCallForm));
